import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Box,
  Checkbox,
  Skeleton,
  TableCell,
  TableRow,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import useAuthScope from "hooks/useAuthScope";
import useZustandStore from "zustandStore";
import { isLicenseManagementEnabled } from "utils/licenseFeature";
import useTableInstance from "hooks/useTableInstance";
import TableField from "components/Table";
import { authStateOption, deviceStatusOption, deviceLinkStatusOption, deviceTypeOption } from "../../const";
import mainStyle from "../../mainStyle.module.scss";
import iconStop from "assets/images/icons/icn_btn_stop.svg";
import iconStart from "assets/images/icons/icn_btn_start.svg";
import iconSettings from "assets/images/icons/icn_btn_settings.svg";
import iconSettingsDisabled from "assets/images/icons/icn_btn_settings_grey.svg";
import iconSearch from "assets/images/icons/icn_search.svg";
import iconStopped from "assets/images/icons/cn_table_cell_stop_red.svg";
import iconSuccess from "assets/images/icons/icn_success.svg";
import { ReactComponent as CheckBoxDefault } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckBoxFilled } from "assets/images/icons/checkbox_fiilled.svg";
import { ReactComponent as CheckBoxClear } from "assets/images/icons/checkbox_clear.svg";
import iconRediscover from "assets/images/icons/icn_btn_rediscover.svg";
import iconLocked from "assets/images/icons/btn_Locked.svg";
import iconUnlocked from "assets/images/icons/btn_Unlock_hover.svg";
import { getDefaultCheckboxProp } from "../../theme";
import BatchAuthenticationModal from "./BatchAuthenticationModal";
import DeviceDrawer from "./DeviceDrawer";
import AdoptionModal from "./AdoptionModal";
import { getSonicosAuthStatus } from "./sonicos";
import {
  makeSonicosAdoptRequest,
  makeSonicosDiscoveryRequest,
  makeSonicosPreConfirmRequest,
  makeSonicosSitesRequest,
  makeSonicosStopRequest,
} from "./requests";

const columnHelper = createColumnHelper();
const MIN_AUTH_SKELETON_DURATION_MS = 800;
const isMockup = process.env.REACT_APP_IS_MOCKUP === "true";
const renderDeviceSkeleton = (width) => <Skeleton variant="rounded" height={12} width={width} />;
const unwrapJsonRpcResult = (response) => response?.result ?? response;

const DataCenterSwitchDiscover = ({
  sendRequest,
  dataMap,
  getData,
  searchVal,
  config,
  changeDiscoverBatchActionsStatus,
  discoverBatchActionTarget,
  setDiscoverBatchActionTarget,
  onAdoptionComplete,
  onOpenSettings,
}) => {
  const { auth, isAuthReady } = useAuthScope();
  const portalConfig = useZustandStore((state) => state.portalConfig);
  const licenseManagementEnabled = isLicenseManagementEnabled(portalConfig);
  const [devices, setDevices] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [hasStartedScan, setHasStartedScan] = useState(false);
  const [scanResponseReceived, setScanResponseReceived] = useState(false);
  const [scanStopped, setScanStopped] = useState(false);
  const [discoverySocketId, setDiscoverySocketId] = useState(null);
  const [authDiscoverySocketIds, setAuthDiscoverySocketIds] = useState([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [drawerDevice, setDrawerDevice] = useState(null);
  const [drawerUsername, setDrawerUsername] = useState("");
  const [drawerPassword, setDrawerPassword] = useState("");
  const [showAdoptModal, setShowAdoptModal] = useState(false);
  const [adoptionSubmitted, setAdoptionSubmitted] = useState(false);
  const [preConfirmSocketId, setPreConfirmSocketId] = useState(null);
  const [preConfirmLoading, setPreConfirmLoading] = useState(false);
  const [showServiceChange, setShowServiceChange] = useState(false);
  const [preConfirmData, setPreConfirmData] = useState([]);
  const [adoptionTargetIds, setAdoptionTargetIds] = useState([]);
  const [showBatchAuthModal, setShowBatchAuthModal] = useState(false);
  const [batchAuthDeviceIds, setBatchAuthDeviceIds] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [sitesSocketId, setSitesSocketId] = useState(null);
  const [adoptSocketId, setAdoptSocketId] = useState(null);
  const processedDevicesRef = useRef(new Set());
  const pendingAuthDiscoveryRef = useRef(new Map());
  const authPendingStartedAtRef = useRef(new Map());
  const scanTimerRef = useRef(null);
  const scanStoppedRef = useRef(false);
  const pendingAdoptionRef = useRef(null);
  const lastAutoStartedScanIdRef = useRef(null);

  const applyAuthStatuses = (statusById) => {
    const updates = [...statusById.entries()];
    if (!updates.length) return;
    const waitMs = Math.max(0, ...updates.map(([id]) => {
      const startedAt = authPendingStartedAtRef.current.get(id);
      return startedAt ? MIN_AUTH_SKELETON_DURATION_MS - (Date.now() - startedAt) : 0;
    }));
    setTimeout(() => {
      const updatesById = new Map(updates);
      setDevices((previous) => previous.map((device) => {
        if (!updatesById.has(device.id)) return device;
        const update = updatesById.get(device.id);
        return {
          ...device,
          ...(typeof update === "object" ? update : { authStatus: update }),
        };
      }));
      updates.forEach(([id]) => authPendingStartedAtRef.current.delete(id));
    }, waitMs);
  };

  const sendDiscoveryAuthAttempt = (targets) => {
    if (!targets.length) return;
    const startedAt = Date.now();
    targets.forEach((device) => authPendingStartedAtRef.current.set(device.id, startedAt));
    const request = makeSonicosDiscoveryRequest({
      auth,
      proxyUuid: config.selectedProxy.proxyUuid,
      entries: targets,
      includeCredentials: true,
    });
    const attempt = {
      targets: targets.map((device) => ({ id: device.id, ipAddress: device.ipAddress })),
      matchedIds: new Set(),
      timeout: setTimeout(() => {
        const pending = pendingAuthDiscoveryRef.current.get(request.socketId);
        if (!pending) return;
        const unresolvedIds = new Set(pending.targets
          .filter((target) => !pending.matchedIds.has(target.id))
          .map((target) => target.id));
        applyAuthStatuses(new Map([...unresolvedIds].map((id) => [id, authStateOption.failed])));
        pendingAuthDiscoveryRef.current.delete(request.socketId);
        setAuthDiscoverySocketIds((previous) => previous.filter((id) => id !== request.socketId));
      }, 30000),
    };
    pendingAuthDiscoveryRef.current.set(request.socketId, attempt);
    sendRequest(request.apiKey, request.socketId, request.placeholder, request.params);
    setAuthDiscoverySocketIds((previous) => [...previous, request.socketId]);
  };

  const startScan = () => {
    pendingAuthDiscoveryRef.current.forEach((attempt) => clearTimeout(attempt.timeout));
    pendingAuthDiscoveryRef.current.clear();
    authPendingStartedAtRef.current.clear();
    setAuthDiscoverySocketIds([]);
    setDevices([]);
    setSelectedDeviceIds([]);
    setDrawerDevice(null);
    setShowAdoptModal(false);
    setAdoptionSubmitted(false);
    setShowServiceChange(false);
    setPreConfirmSocketId(null);
    setPreConfirmLoading(false);
    pendingAdoptionRef.current = null;
    setAdoptionTargetIds([]);
    setAdoptSocketId(null);
    setScanStopped(false);
    setScanResponseReceived(false);
    setHasStartedScan(true);
    scanStoppedRef.current = false;
    setScanning(true);
    processedDevicesRef.current = new Set();
    const request = makeSonicosDiscoveryRequest({
      auth,
      proxyUuid: config.selectedProxy.proxyUuid,
      entries: config.entries,
      includeCredentials: true,
    });
    setDiscoverySocketId(request.socketId);
    sendRequest(request.apiKey, request.socketId, request.placeholder, request.params);
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    scanTimerRef.current = setTimeout(() => {
      setScanning(false);
      setScanResponseReceived(true);
    }, 30000);
  };

  useEffect(() => () => {
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    pendingAuthDiscoveryRef.current.forEach((attempt) => clearTimeout(attempt.timeout));
    pendingAuthDiscoveryRef.current.clear();
  }, []);

  useEffect(() => {
    if (!config.autoStart || !isAuthReady || !auth.scopeLevel || !auth.realm?.id) return;
    if (lastAutoStartedScanIdRef.current === config.scanId) return;
    lastAutoStartedScanIdRef.current = config.scanId;
    startScan();
    // Add Device entry starts the new scan after Save. Settings edits use
    // autoStart=false and wait for the user to press Start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, config.autoStart, config.scanId, isAuthReady]);

  useEffect(() => {
    if (scanStoppedRef.current || !discoverySocketId || !dataMap[discoverySocketId]) return;
    const response = getData(discoverySocketId);
    const result = unwrapJsonRpcResult(response);
    if (!result || !result.resource?.includes("discovery")) return;
    setScanResponseReceived(true);
    const discovered = Array.isArray(result.data) ? result.data : [];
    if (discovered.length === 0) {
      setScanning(false);
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
      return;
    }

    const additions = [];
    discovered.forEach((raw) => {
      const ipAddress = raw.deviceIPAddress || raw.ipAddress || "";
      const entry = config.entries.find((item) => item.ipAddress.trim() === ipAddress);
      if (!entry) return;
      const authStatus = getSonicosAuthStatus(raw);
      if (authStatus === null) return;
      const macAddress = raw.macAddress || "";
      const identity = (macAddress || ipAddress).toLowerCase();
      if (processedDevicesRef.current.has(identity)) return;
      processedDevicesRef.current.add(identity);
      const device = {
        ...raw,
        id: uuidv4(),
        checked: false,
        macAddress,
        ipAddress,
        modelName: raw.modelName || "",
        hardwareVersion: raw.hardwareVersion || "",
        version: raw.version || "",
        protocolType: "sonicos",
        productCategory: "Switch",
        deviceType: deviceTypeOption.switch,
        status: deviceStatusOption.unmanaged,
        linkStatus: deviceLinkStatusOption.online,
        authStatus,
        username: entry.username,
        password: entry.password,
        role: entry.role || "Spine",
        provisioning: false,
      };
      additions.push(device);
    });
    if (additions.length) {
      setDevices((previous) => [...previous, ...additions]);
    }
    // The mock data source returns one complete result set instead of the
    // backend's streamed rows followed by a final empty array.
    if (isMockup) {
      setScanning(false);
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    }
  }, [config.entries, config.selectedProxy.proxyUuid, dataMap, discoverySocketId]);

  useEffect(() => {
    authDiscoverySocketIds.forEach((socketId) => {
      if (!dataMap[socketId]) return;
      const response = getData(socketId);
      const attempt = pendingAuthDiscoveryRef.current.get(socketId);
      if (!response || !response.resource?.includes("discovery") || !attempt) return;
      const results = Array.isArray(response.data) ? response.data : [];
      if (results.length === 0) {
        const unresolvedIds = new Set(attempt.targets
          .filter((target) => !attempt.matchedIds.has(target.id))
          .map((target) => target.id));
        applyAuthStatuses(new Map([...unresolvedIds].map((id) => [id, authStateOption.failed])));
        clearTimeout(attempt.timeout);
        pendingAuthDiscoveryRef.current.delete(socketId);
        setAuthDiscoverySocketIds((previous) => previous.filter((id) => id !== socketId));
        return;
      }

      const matchedResults = attempt.targets.flatMap((target) => {
        const result = results.find((item) => (item.deviceIPAddress || item.ipAddress) === target.ipAddress);
        if (!result) return [];
        attempt.matchedIds.add(target.id);
        const authStatus = getSonicosAuthStatus(result);
        if (authStatus === null) return [];
        return [{
          id: target.id,
          authStatus,
          ...(result.macAddress ? { macAddress: result.macAddress } : {}),
          ...(result.modelName ? { modelName: result.modelName } : {}),
          ...(result.version ? { version: result.version } : {}),
          ...(result.hardwareVersion ? { hardwareVersion: result.hardwareVersion } : {}),
          ...(result.error ? { discoveryError: result.error } : { discoveryError: null }),
        }];
      });
      if (matchedResults.length) {
        applyAuthStatuses(new Map(matchedResults.map(({ id, ...update }) => [id, update])));
      }
    });
  }, [authDiscoverySocketIds, dataMap]);

  useEffect(() => {
    if (!isAuthReady || !auth.scopeLevel || !auth.realm?.id) return;
    if (auth.scopeLevel === "site") {
      setSites([{ id: auth.sites?.id, name: auth.sites?.name }].filter((site) => site.id));
      setSelectedSiteId(auth.sites?.id || "");
    } else if (!sitesSocketId) {
      const request = makeSonicosSitesRequest(auth);
      setSitesSocketId(request.socketId);
      sendRequest(request.apiKey, request.socketId, request.placeholder, request.params);
    }
  }, [auth, auth.scopeLevel, auth.realm?.id, auth.sites?.id, auth.sites?.name, isAuthReady]);

  useEffect(() => {
    if (!sitesSocketId || !dataMap[sitesSocketId]) return;
    const response = getData(sitesSocketId);
    const rows = response?.data?.sites || [];
    const nextSites = rows.map((site) => ({ id: site.siteId, name: site.siteName }));
    setSites(nextSites);
  }, [dataMap, sitesSocketId]);

  const submitAdoption = useCallback((targets, siteId) => {
    if (!siteId || !targets?.length) return;
    const targetIds = targets.map((device) => device.id);
    setAdoptionTargetIds(targetIds);
    setAdoptionSubmitted(true);
    setDevices((previous) => previous.map((device) => targetIds.includes(device.id)
      ? { ...device, adoptResult: null }
      : device));
    const request = makeSonicosAdoptRequest({
      auth,
      siteId,
      proxyUuid: config.selectedProxy.proxyUuid,
      devices: targets,
    });
    sendRequest(request.apiKey, request.socketId, request.placeholder, request.params);
    setAdoptSocketId(request.socketId);
  }, [auth, config.selectedProxy.proxyUuid, sendRequest]);

  useEffect(() => {
    if (!preConfirmSocketId || !dataMap[preConfirmSocketId]) return;
    const response = unwrapJsonRpcResult(getData(preConfirmSocketId));
    if (!response) return;
    setPreConfirmSocketId(null);
    setPreConfirmLoading(false);
    const result = Array.isArray(response.data) ? response.data : [];
    const hasSubscribedService = result.some((item) => item.service?.length > 0);
    if (hasSubscribedService) {
      setPreConfirmData(result);
      setShowServiceChange(true);
      return;
    }
    const pending = pendingAdoptionRef.current;
    pendingAdoptionRef.current = null;
    if (pending) submitAdoption(pending.targets, pending.siteId);
  }, [dataMap, getData, preConfirmSocketId, submitAdoption]);

  useEffect(() => {
    if (!adoptSocketId || !dataMap[adoptSocketId]) return;
    const response = getData(adoptSocketId);
    if (!response?.resource?.includes("/provision/adopt")) return;
    const results = Array.isArray(response.data) ? response.data : [];
    if (!results.length) return;
    setDevices((previous) => previous.map((device) => {
      if (!adoptionTargetIds.includes(device.id)) return device;
      const result = results.find((item) => (
        (item.macAddress || "").toLowerCase() === (device.macAddress || "").toLowerCase()
      ));
      if (!result) return device;
      const adopted = result.code === 0;
      return {
        ...device,
        adoptResult: { code: result.code, message: result.message || "" },
        ...(adopted ? { status: deviceStatusOption.provisioning, provisioning: true } : {}),
        checked: false,
      };
    }));
  }, [adoptSocketId, adoptionTargetIds, dataMap]);

  const selectedDevices = devices.filter((device) => selectedDeviceIds.includes(device.id));
  const adoptionDevices = devices.filter((device) => (
    (adoptionTargetIds.length ? adoptionTargetIds : selectedDeviceIds).includes(device.id)
  ));
  const activeDrawerDevice = drawerDevice
    ? devices.find((device) => device.id === drawerDevice.id) || drawerDevice
    : null;
  const selectedAuthStatus = selectedDevices[0]?.authStatus;
  const canAuthenticate = selectedDevices.length > 0 && selectedDevices.every((device) => device.authStatus === authStateOption.failed);
  const canAdopt = selectedDevices.length > 0 && selectedDevices.every((device) => device.authStatus === authStateOption.passed);

  useEffect(() => {
    changeDiscoverBatchActionsStatus({ auth: canAuthenticate, adopt: canAdopt });
  }, [canAuthenticate, canAdopt, changeDiscoverBatchActionsStatus]);

  useEffect(() => () => {
    changeDiscoverBatchActionsStatus(null);
  }, [changeDiscoverBatchActionsStatus]);

  const toggleDevice = (id) => {
    const target = devices.find((device) => device.id === id);
    if (!target || target.authStatus == null) return;
    setSelectedDeviceIds((previous) => (
      previous.includes(id)
        ? previous.filter((item) => item !== id)
        : previous.length === 0 || devices.find((item) => previous.includes(item.id))?.authStatus === target.authStatus
          ? [...previous, id]
          : previous
    ));
  };

  const toggleAll = (checked) => {
    const targetAuthStatus = selectedAuthStatus
      ?? devices.find((device) => device.authStatus != null)?.authStatus;
    if (!targetAuthStatus) return;
    setSelectedDeviceIds(checked
      ? devices.filter((device) => device.authStatus === targetAuthStatus).map((device) => device.id)
      : []);
  };

  const handleBatchAuthenticate = () => {
    setBatchAuthDeviceIds(selectedDeviceIds);
    setShowBatchAuthModal(true);
  };

  const submitBatchAuthentication = (username, password) => {
    const targets = devices
      .filter((device) => batchAuthDeviceIds.includes(device.id))
      .map((device) => ({ ...device, username, password }));
    setDevices((previous) => previous.map((device) => batchAuthDeviceIds.includes(device.id)
      ? { ...device, authStatus: null, username, password, checked: false }
      : device));
    sendDiscoveryAuthAttempt(targets);
    setSelectedDeviceIds([]);
  };

  const retryAuthentication = (device) => {
    const credentials = {
      ...device,
      username: drawerUsername.trim(),
      password: drawerPassword,
    };
    setDevices((previous) => previous.map((item) => item.id === device.id
      ? { ...item, authStatus: null, username: credentials.username, password: credentials.password }
      : item));
    sendDiscoveryAuthAttempt([credentials]);
  };

  useEffect(() => {
    if (!discoverBatchActionTarget) return;
    if (discoverBatchActionTarget === "auth" && canAuthenticate) {
      handleBatchAuthenticate();
    } else if (discoverBatchActionTarget === "adopt" && canAdopt) {
      setAdoptionTargetIds(selectedDeviceIds);
      setAdoptionSubmitted(false);
      setSelectedSiteId(auth.scopeLevel === "org" ? "" : auth.sites?.id || "");
      setShowAdoptModal(true);
    }
    setDiscoverBatchActionTarget(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoverBatchActionTarget, canAuthenticate, canAdopt]);

  const handleAdopt = () => {
    if (!selectedSiteId) return;
    const targets = adoptionDevices.filter((device) => device.authStatus === authStateOption.passed);
    if (!targets.length) return;
    if (!licenseManagementEnabled) {
      submitAdoption(targets, selectedSiteId);
      return;
    }
    const request = makeSonicosPreConfirmRequest({ auth, siteId: selectedSiteId, devices: targets });
    pendingAdoptionRef.current = { targets, siteId: selectedSiteId };
    setPreConfirmLoading(true);
    setPreConfirmSocketId(request.socketId);
    sendRequest(request.apiKey, request.socketId, request.placeholder, request.params);
  };

  const stopScan = () => {
    scanStoppedRef.current = true;
    const request = makeSonicosStopRequest({ auth });
    sendRequest(request.apiKey, request.socketId, request.placeholder, request.params);
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    setScanning(false);
    setScanResponseReceived(true);
    setScanStopped(true);
  };

  const restartScan = () => {
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    if (!isAuthReady || !auth.scopeLevel || !auth.realm?.id) return;
    startScan();
  };

  const columns = useMemo(() => [
    columnHelper.display({
      id: "checkbox",
      size: 50,
      header: () => (
        <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: "100%", ml: "-6px" }}>
          {devices.some((device) => device.authStatus != null) && (
            <Checkbox
              icon={<CheckBoxDefault style={{ width: 24, height: 24 }} />}
              indeterminateIcon={<CheckBoxClear style={{ width: 24, height: 24 }} />}
              checkedIcon={<CheckBoxFilled style={{ width: 24, height: 24 }} />}
              checked={Boolean(selectedAuthStatus) && devices
                .filter((device) => device.authStatus === selectedAuthStatus)
                .every((device) => selectedDeviceIds.includes(device.id))}
              indeterminate={Boolean(selectedAuthStatus) && devices
                .filter((device) => device.authStatus === selectedAuthStatus)
                .some((device) => !selectedDeviceIds.includes(device.id))}
              onMouseDown={(event) => event.preventDefault()}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              onChange={(event, checked) => {
                event.stopPropagation();
                toggleAll(checked);
              }}
              sx={getDefaultCheckboxProp().sx}
            />
          )}
        </Box>
      ),
      cell: ({ row }) => {
        const device = row.original;
        if (device.authStatus == null) return renderDeviceSkeleton(24);
        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            <Checkbox
              edge="start"
              icon={<CheckBoxDefault style={{ width: 24, height: 24 }} />}
              checkedIcon={<CheckBoxFilled style={{ width: 24, height: 24 }} />}
              checked={selectedDeviceIds.includes(device.id)}
              disabled={device.authStatus == null || (selectedAuthStatus != null && selectedAuthStatus !== device.authStatus)}
              onClick={(event) => event.stopPropagation()}
              onChange={() => toggleDevice(device.id)}
              sx={getDefaultCheckboxProp().sx}
            />
          </Box>
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor("status.label", {
      header: "Status",
      size: 100,
      cell: ({ row }) => {
        if (row.original.authStatus == null) return renderDeviceSkeleton(90);
        const status = row.original.status;
        return <Box className={`${mainStyle["chip-name"]} ${mainStyle[`${status.value}-chip`]}`}>{status.label}</Box>;
      },
    }),
    columnHelper.accessor("authStatus", {
      header: "Auth",
      size: 75,
      cell: ({ getValue, row }) => {
        if (row.original.authStatus == null) return renderDeviceSkeleton(24);
        const value = getValue();
        const icon = value === authStateOption.passed ? iconUnlocked : value === authStateOption.failed ? iconLocked : iconRediscover;
        return <img src={icon} alt="" style={{ width: 20, height: 20, marginRight: 5 }} />;
      },
    }),
    columnHelper.accessor("modelName", {
      header: "Model Name",
      cell: ({ getValue, row }) => row.original.authStatus == null ? renderDeviceSkeleton(140) : getValue(),
    }),
    columnHelper.accessor("macAddress", {
      header: "MAC Address",
      cell: ({ getValue, row }) => row.original.authStatus == null ? renderDeviceSkeleton(150) : getValue(),
    }),
    columnHelper.accessor("ipAddress", {
      header: "IP Address",
      cell: ({ getValue, row }) => row.original.authStatus == null ? renderDeviceSkeleton(120) : getValue(),
    }),
  ], [devices, selectedAuthStatus, selectedDeviceIds]);

  const table = useTableInstance({
    tableName: "dataCenterSwitchDiscover",
    data: devices,
    columns,
    enableGlobalFilter: true,
    enableRowSelection: false,
  });

  useEffect(() => {
    table.setGlobalFilter(searchVal || "");
  }, [searchVal]);

  return (
    <Box>
      <Box className={mainStyle["tools-container"]}>
        <Box className={mainStyle["scan-status"]}>
          <Box className={`${mainStyle.progress} ${scanning ? mainStyle.processing : scanStopped ? mainStyle.stopped : hasStartedScan ? mainStyle.done : ""}`}>
            <img src={scanning ? iconSearch : scanStopped ? iconStopped : hasStartedScan ? iconSuccess : iconStart} alt="" />
            {scanning ? "Scanning..." : scanStopped ? "Scan Stopped" : hasStartedScan ? "Scan Completed" : "Ready to Scan"}
          </Box>
        </Box>
        {scanning ? (
          <>
            <Box className={mainStyle["scan-btn"]} onClick={stopScan}>
              <img src={iconStop} alt="" />
              <span>Stop</span>
            </Box>
            <Box className={`${mainStyle["scan-btn"]} ${mainStyle["scan-btn-disabled"]}`}>
              <img src={iconSettingsDisabled} alt="" />
              <span>Discover Settings</span>
            </Box>
          </>
        ) : (
          <>
            <Box className={mainStyle["scan-btn"]} onClick={restartScan}>
              <img src={iconStart} alt="" />
              <span>Start</span>
            </Box>
            <Box className={mainStyle["scan-btn"]} onClick={onOpenSettings}>
              <img src={iconSettings} alt="" />
              <span>Discover Settings</span>
            </Box>
          </>
        )}
      </Box>

      <TableField
        {...table}
        drawerOpen={Boolean(drawerDevice)}
        pendingChildren={scanning && !scanResponseReceived ? ({ headers }) => (
          Array.from({ length: 6 }).map((_, rowIndex) => (
            <TableRow key={`skeleton-${rowIndex}`}>
              {headers.map((header) => (
                <TableCell key={header.id} sx={{ width: 200 }}>
                  <Skeleton
                    variant="rounded"
                    height={10}
                    width={header.id === "checkbox" ? 24 : "80%"}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : !scanning && hasStartedScan && devices.length === 0 ? "No Entity in the table" : null}
        handleRowOnClick={(row) => {
          const device = devices.find((item) => item.id === row.id) || row;
          if (device?.authStatus != null) {
            setDrawerDevice(device);
            setDrawerUsername(device.username || "");
            setDrawerPassword(device.password || "");
          }
        }}
      />

      <DeviceDrawer
        open={Boolean(drawerDevice)}
        device={activeDrawerDevice}
        username={drawerUsername}
        setUsername={setDrawerUsername}
        password={drawerPassword}
        setPassword={setDrawerPassword}
        onClose={() => setDrawerDevice(null)}
        onAuthenticate={() => {
          retryAuthentication(activeDrawerDevice);
          setDrawerDevice(null);
        }}
        onAdopt={() => {
          setSelectedDeviceIds([]);
          setAdoptionTargetIds([activeDrawerDevice.id]);
          setAdoptionSubmitted(false);
          setSelectedSiteId(auth.scopeLevel === "org" ? "" : auth.sites?.id || "");
          setShowAdoptModal(true);
        }}
      />

      <AdoptionModal
        open={showAdoptModal}
        devices={adoptionDevices}
        sites={sites}
        scopeLevel={auth.scopeLevel}
        selectedSiteId={selectedSiteId}
        setSelectedSiteId={setSelectedSiteId}
        submitted={adoptionSubmitted}
        showServiceChange={showServiceChange}
        preConfirmData={preConfirmData}
        preConfirmLoading={preConfirmLoading}
        onAdopt={handleAdopt}
        onConfirmServiceChange={() => {
          const pending = pendingAdoptionRef.current;
          pendingAdoptionRef.current = null;
          setShowServiceChange(false);
          if (pending) submitAdoption(pending.targets, pending.siteId);
        }}
        onCancelServiceChange={() => {
          pendingAdoptionRef.current = null;
          setShowServiceChange(false);
          setPreConfirmData([]);
        }}
        onClose={() => {
          if (adoptionSubmitted && adoptionDevices.some((device) => device.adoptResult?.code === 0)) {
            onAdoptionComplete?.();
          }
          pendingAdoptionRef.current = null;
          setShowAdoptModal(false);
          setSelectedDeviceIds([]);
          setAdoptionTargetIds([]);
          setAdoptionSubmitted(false);
          setShowServiceChange(false);
          setPreConfirmSocketId(null);
          setPreConfirmLoading(false);
        }}
      />

      <BatchAuthenticationModal
        open={showBatchAuthModal}
        devices={devices.filter((device) => batchAuthDeviceIds.includes(device.id))}
        onClose={() => {
          setShowBatchAuthModal(false);
          setBatchAuthDeviceIds([]);
        }}
        onSubmit={submitBatchAuthentication}
      />
    </Box>
  );
};

export default DataCenterSwitchDiscover;
